import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

/**
 * Represents the state of the user's poker session.
 * This information is obtained after successfully joining a table.
 */
const SESSION_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

interface PokerSessionState {
  room: string | null;
  playerId: string | null;
  token: string | null;
  sessionCreatedAt: number | null;
}

/**
 * Defines the actions that can be performed to modify the session state.
 */
interface PokerSessionActions {
  /**
   * Sets the session details after a successful join.
   * @param session - An object containing the room, playerId, and token.
   */
  setSession: (session: { room: string; playerId: string; token: string }) => void;

  /**
   * Clears the session details, effectively logging the user out of a table.
   */
  clearSession: () => void;
}

const initialState: PokerSessionState = {
  room: null,
  playerId: null,
  token: null,
  sessionCreatedAt: null,
};

/**
 * Zustand store for managing the poker game session.
 *
 * This store persists the session information to localStorage, allowing the state
 * to be rehydrated after a page refresh.
 *
 * @example
 * const { room, playerId, setSession } = usePokerStore();
 */
export const usePokerStore = create<PokerSessionState & PokerSessionActions>()(
  persist(
    (set) => ({
      ...initialState,

      setSession: (session) => {
        set({
          room: session.room,
          playerId: session.playerId,
          token: session.token,
          sessionCreatedAt: Date.now(),
        });
      },

      clearSession: () => {
        set(initialState);
        localStorage.removeItem('poker-session-storage');
      },
    }),
    {
      name: 'poker-session-storage',
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        if (state?.sessionCreatedAt && Date.now() - state.sessionCreatedAt > SESSION_MAX_AGE_MS) {
          state.room = null;
          state.playerId = null;
          state.token = null;
          state.sessionCreatedAt = null;
          localStorage.removeItem('poker-session-storage');
        }
      },
    }
  )
);
